import {
  Box,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  PinInput,
  Stack,
  Text,
} from '@chakra-ui/react';
import { Form, FormikProvider, useFormik } from 'formik';
import { useEffect, useRef } from 'react';
import * as Yup from 'yup';

import {
  CustomFormErrorMessage,
  CustomPinInputField,
  PrimaryButton,
} from '../../../../../components/custom';
import ResendTimer from '../../../../../components/utils/ResendTimer';
import { otpValidation } from '../../../../../constants/validationConstants';
import { TMfaType } from '../../../../../store/slices/security/mfaTypes';
import { channelMeta } from './channelMeta';

const RESEND_COOLDOWN_MS = 60_000;

interface OtpStepProps {
  channel: TMfaType;
  helperText?: string;
  submitLabel?: string;
  loading?: boolean;
  resending?: boolean;
  hideHelper?: boolean;
  rightAdornment?: React.ReactNode;
  onSubmit: (token: string) => Promise<void> | void;
  onResend?: () => Promise<void> | void;
}

export default function OtpStep({
  channel,
  helperText,
  submitLabel = 'Verify',
  loading,
  resending,
  hideHelper,
  rightAdornment,
  onSubmit,
  onResend,
}: OtpStepProps) {
  const formik = useFormik({
    initialValues: { token: '' },
    validationSchema: Yup.object().shape({ token: otpValidation }),
    onSubmit: async ({ token }, helpers) => {
      try {
        await onSubmit(token);
      } catch {
        helpers.setFieldValue('token', '');
      }
    },
  });

  const {
    values,
    errors,
    touched,
    isValid,
    dirty,
    handleSubmit,
    setFieldValue,
    resetForm,
  } = formik;

  const prevChannelRef = useRef(channel);
  useEffect(() => {
    if (prevChannelRef.current !== channel) {
      resetForm();
      prevChannelRef.current = channel;
    }
  }, [channel, resetForm]);

  const meta = channelMeta[channel];
  const helper =
    helperText ??
    (channel === 'authenticator'
      ? 'Enter the 6-digit code from your authenticator app.'
      : `We sent a 6-digit code via ${meta.label.toLowerCase()}.`);

  const showResend = channel !== 'authenticator' && Boolean(onResend);

  return (
    <FormikProvider value={formik}>
      <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
        <Stack spacing={5}>
          {!hideHelper && (
            <Text color="gray.300" fontSize=".875rem">
              {helper}
            </Text>
          )}

          <FormControl
            id="mfa-token"
            isInvalid={Boolean(touched.token && errors.token)}
          >
            <Flex justify="space-between" align="baseline" mb={2}>
              <FormLabel mb={0}>Verification Code</FormLabel>
              {rightAdornment}
            </Flex>
            <HStack>
              <PinInput
                placeholder="0"
                value={values.token}
                onChange={(value) => setFieldValue('token', value)}
                isInvalid={Boolean(touched.token && errors.token)}
              >
                {Array.from({ length: 6 }).map((_, index) => (
                  <CustomPinInputField
                    key={index}
                    data-testid={`mfa-otp-input-${index + 1}`}
                  />
                ))}
              </PinInput>
            </HStack>
            <CustomFormErrorMessage>
              {touched.token && errors.token}
            </CustomFormErrorMessage>
          </FormControl>

          {showResend && (
            <Box display="flex" alignItems="center" gap={2}>
              <Text fontSize=".875rem" color="gray.300">
                Didn&apos;t get a Code?
              </Text>
              <ResendTimer
                title="Resend"
                toolTipLabel="OTP resent"
                loading={Boolean(resending)}
                resendAction={async () => {
                  if (onResend) {
                    await onResend();
                    setFieldValue('token', '');
                  }
                }}
                countdownTime={RESEND_COOLDOWN_MS}
              />
            </Box>
          )}

          <PrimaryButton
            type="submit"
            isDisabled={!isValid || !dirty}
            isLoading={Boolean(loading)}
          >
            {submitLabel}
          </PrimaryButton>
        </Stack>
      </Form>
    </FormikProvider>
  );
}
